import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { COMPANY } from "@/lib/company";
import { AppHeader } from "@/components/AppHeader";
import { fmtMoney } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, FileText, Copy, Loader2, Eye, Pencil, Trash2,
  Layers, Files, TrendingUp,
} from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/quotations");
      setQuotes(data);
    } catch (e) {
      setQuotes([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await api.delete(`/quotations/${id}`);
      setQuotes((q) => q.filter((x) => x.id !== id));
      toast.success("Quotation deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const doClone = async (id) => {
    try {
      const { data } = await api.post(`/quotations/${id}/clone`);
      setQuotes((q) => [data, ...q]);
      toast.success(`Cloned as ${data.quote_no}`);
      navigate(`/quotation/${data.id}/edit`);
    } catch (e) {
      toast.error("Failed to clone");
    }
  };

  const doSetStatus = async (id, status) => {
    setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.patch(`/quotations/${id}/status`, { status });
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error("Failed to update status");
      load();
    }
  };

  const totalValue = (quotes || []).reduce((s, q) => s + (q.totals?.grand_total || 0), 0);
  const totalUnits = (quotes || []).reduce((s, q) => s + (q.totals?.total_units || 0), 0);

  const stats = [
    { label: "Quotations", value: quotes ? quotes.length : "—", icon: Files, accent: "text-blue-600" },
    { label: "Pipeline Value", value: quotes ? `₹ ${fmtMoney(totalValue)}` : "—", icon: TrendingUp, accent: "text-emerald-600" },
    { label: "Units Quoted", value: quotes ? totalUnits : "—", icon: Layers, accent: "text-amber-600" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Quotations
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {COMPANY.system}
            </p>
          </div>
          <Button
            data-testid="new-quotation-button"
            onClick={() => navigate("/quotation/new")}
            className="rounded-sm bg-blue-600 hover:bg-blue-700 h-11 px-5"
          >
            <Plus className="w-4 h-4 mr-2" /> New Quotation
          </Button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="bg-white border border-slate-200 rounded-sm p-5 flex items-center justify-between"
            >
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">{s.label}</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{s.value}</div>
              </div>
              <s.icon className={`w-8 h-8 ${s.accent} opacity-80`} />
            </motion.div>
          ))}
        </div>

        {/* List */}
        {quotes === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm py-20 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No quotations yet.</p>
            <Button
              data-testid="empty-new-quotation-button"
              onClick={() => navigate("/quotation/new")}
              className="rounded-sm bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" /> Create your first quotation
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto thin-scroll">
              <table className="w-full text-sm" data-testid="quotations-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                    <th className="text-left font-semibold px-4 py-3">Quote No</th>
                    <th className="text-left font-semibold px-4 py-3">Project / Client</th>
                    <th className="text-left font-semibold px-4 py-3">Date</th>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                    <th className="text-right font-semibold px-4 py-3">Items</th>
                    <th className="text-right font-semibold px-4 py-3">Grand Total</th>
                    <th className="text-right font-semibold px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q, i) => (
                    <motion.tr
                      key={q.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      data-testid={`quotation-row-${q.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors group"
                    >
                      <td className="px-4 py-3 font-mono text-slate-800 font-medium whitespace-nowrap">
                        {q.quote_no}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">
                          {q.project_title || "Untitled Project"}
                        </div>
                        <div className="text-xs text-slate-500">{q.client_name || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{q.quote_date}</td>
                      <td className="px-4 py-3">
                        <Select value={q.status || "draft"} onValueChange={(v) => doSetStatus(q.id, v)}>
                          <SelectTrigger
                            data-testid={`status-${q.id}`}
                            className={`h-7 w-[108px] rounded-full border-0 text-xs font-medium capitalize ${STATUS_STYLES[q.status || "draft"]}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="sent">Sent</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-700">
                        {q.items?.length || 0}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900 whitespace-nowrap">
                        ₹ {fmtMoney(q.totals?.grand_total)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`preview-${q.id}`}
                            onClick={() => navigate(`/quotation/${q.id}/preview`)}
                            className="h-8 w-8 rounded-sm text-slate-500 hover:text-blue-600"
                            title="Preview / PDF"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`clone-${q.id}`}
                            onClick={() => doClone(q.id)}
                            className="h-8 w-8 rounded-sm text-slate-500 hover:text-emerald-600"
                            title="Clone"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-${q.id}`}
                            onClick={() => navigate(`/quotation/${q.id}/edit`)}
                            className="h-8 w-8 rounded-sm text-slate-500 hover:text-slate-900"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-${q.id}`}
                            onClick={() => setDeleteId(q.id)}
                            className="h-8 w-8 rounded-sm text-slate-500 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quotation?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The quotation will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={doDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
